import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useVehicles } from '../../../hooks/useVehicles';
import { useMemo } from 'react';
import { createTrustedHTML } from '../../../lib/trustedTypes';

export default function ImpactStats() {
  const { ref, isVisible } = useScrollAnimation();
  const { vehicles, loading } = useVehicles();

  const { soldCount, totalDonated } = useMemo(() => {
    const sold = vehicles.filter(v => v.status === 'sold').length;
    return {
      soldCount: sold,
      totalDonated: sold * 100,
    };
  }, [vehicles]);

  const displaySold = loading ? 0 : soldCount;
  const displayDonated = loading ? 0 : totalDonated;

  const stats = [
    {
      icon: 'ri-car-line',
      value: loading ? '...' : displaySold.toString(),
      label: 'Cars Sold',
      description: 'Supporting rescue efforts',
      isLink: false,
    },
    {
      icon: 'ri-money-dollar-circle-line',
      value: loading ? '...' : `$${displayDonated.toLocaleString()}`,
      label: 'Donated to ODR',
      description: 'To Oregon Dog Rescue',
      isLink: false,
    },
    {
      icon: 'ri-heart-pulse-line',
      value: 'Visit ODR',
      label: 'Dogs Rescued',
      description: 'See official count at oregondogrescue.org',
      isLink: true,
    },
    {
      icon: 'ri-shield-star-line',
      value: '$300',
      label: 'Military Discount',
      description: 'For service members & spouses',
      isLink: false,
      linkTo: '/military-discount',
    },
  ];

  // NGO + DonateAction structured data
  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'NGO',
        name: 'Rockhound Auto — Oregon Dog Rescue Partnership',
        description:
          'Rockhound Auto donates $100 from every vehicle sale to Oregon Dog Rescue, a no-kill foster-based shelter in the Pacific Northwest.',
        url: 'https://rockhoundauto.com/community-impact',
        knowsAbout: 'Oregon Dog Rescue',
        memberOf: {
          '@type': 'Organization',
          name: 'Oregon Dog Rescue',
          url: 'https://www.oregondogrescue.org',
        },
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Cars Sold', value: displaySold.toString() },
          { '@type': 'PropertyValue', name: 'Total Donated', value: `$${displayDonated.toLocaleString()}` },
          { '@type': 'PropertyValue', name: 'Donation Per Sale', value: '$100' },
          { '@type': 'PropertyValue', name: 'Commitment', value: '100% of every sale supports rescue' },
          { '@type': 'PropertyValue', name: 'Military Discount', value: '$300 for service members and spouses' },
        ],
      },
      {
        '@type': 'DonateAction',
        agent: {
          '@type': 'AutoDealer',
          name: 'Rockhound Auto',
          url: 'https://rockhoundauto.com',
        },
        recipient: {
          '@type': 'NGO',
          name: 'Oregon Dog Rescue',
          url: 'https://www.oregondogrescue.org',
          description: 'No-kill, foster-based dog rescue shelter serving the Pacific Northwest.',
        },
        description:
          '$100 is donated to Oregon Dog Rescue for every vehicle sold at Rockhound Auto.',
        price: '100',
        priceCurrency: 'USD',
        actionStatus: 'https://schema.org/ActiveActionStatus',
      },
    ],
  };

  return (
    <section className="py-20 bg-white" aria-label="Community impact statistics">
      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: createTrustedHTML(JSON.stringify(schemaData)) }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <dl ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`bg-[#F7F5F0] rounded-xl p-6 text-center border border-[#E8E2D6] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-14 h-14 bg-[#E8B84B] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className={`${stat.icon} text-2xl text-white`} aria-hidden="true"></i>
              </div>

              <dt className="font-['Oswald'] text-lg font-semibold text-[#1A2332] mb-2">
                {stat.label}
              </dt>

              <dd className="font-['Oswald'] text-4xl font-bold text-[#1A2332] mb-2">
                {stat.isLink ? (
                  <a
                    href="https://www.oregondogrescue.org"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label="Visit Oregon Dog Rescue website (opens in new tab)"
                    className="hover:text-[#E8B84B] transition-colors underline text-2xl"
                  >
                    Visit ODR
                  </a>
                ) : stat.linkTo ? (
                  <a
                    href={stat.linkTo}
                    aria-label={`Learn more about ${stat.label}`}
                    className="hover:text-[#E8B84B] transition-colors"
                  >
                    {stat.value}
                  </a>
                ) : (
                  stat.value
                )}
              </dd>

              <p className="text-sm text-[#4A5568]">{stat.description}</p>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}